import type { Calendar } from "@event-calendar/core";
import { nextLessonToSchedule } from "@pwz/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useApiClient } from "../../api/ApiClientContext.js";
import { ApiClientError, isStaleOrConflict } from "../../api/errors.js";
import type {
  ManagerCalendarEvent,
  ScheduledLessonRecord,
  UnscheduledLesson,
} from "../../api/schemas.js";
import { EventCalendarAdapter } from "../../calendar/EventCalendarAdapter.js";
import { useCalendarFit } from "../../calendar/useCalendarFit.js";
import { createIdempotencyKey } from "../../lib/idempotency.js";
import {
  dayRangeInTimeZone,
  getDeviceTimeZone,
  monthRangeInTimeZone,
  shortZoneName,
  toIsoUtc,
  utcOffsetLabel,
  wallClockToIso,
  weekRangeInTimeZone,
} from "../../lib/timezone.js";
import { EmptyState } from "../../ui/EmptyState.js";
import { LoadingState } from "../../ui/LoadingState.js";
import { SurfaceShell } from "../../ui/SurfaceShell.js";
import { toAccessibleError } from "../../ui/toAccessibleError.js";
import { signInReasonFor } from "../auth/reason.js";
import { SignInScreen } from "../auth/SignInScreen.js";
import {
  filterEventsByLocation,
  filterInstructorsByLocation,
  filterVenuesByLocation,
  readStoredLocationId,
  resolveActiveLocation,
  storeLocationId,
  venueLocationLookup,
} from "./model/location.js";
import {
  capacityForEvent,
  computeCapacitySnapshots,
  filterEventsByVenues,
  paintForInstructor,
  toCalendarEvents,
  toCalendarResources,
} from "./model/map-events.js";
import { formatRangeLabel, shiftAnchor } from "./model/range-label.js";
import type {
  AccessibleError,
  CalendarViewMode,
  LessonDialogState,
  ManagerSurfaceMode,
} from "./model/types.js";
import { useMobileCalendarView } from "./model/useMobileCalendarView.js";
import { CalendarToolbar, type DealHeaderSummary } from "./ui/CalendarToolbar.js";
import { DealDrawer, type DealDrawerTab } from "./ui/DealDrawer.js";
import { ErrorBanner } from "./ui/ErrorBanner.js";
import { LessonDetailSheet } from "./ui/LessonDetailSheet.js";
import { LessonFormDialog, type LessonFormValues } from "./ui/LessonFormDialog.js";

import { ReasonDialog } from "./ui/ReasonDialog.js";
import { UnscheduledQueueDialog } from "./ui/UnscheduledQueueDialog.js";

export interface ManagerCalendarProps {
  readonly mode?: ManagerSurfaceMode;
  readonly leadId?: number | null;
  readonly initialView?: CalendarViewMode;
  readonly initialDate?: Date;
}

export function rangeFor(
  view: CalendarViewMode,
  anchor: Date,
  timeZone: string,
): { from: string; to: string } {
  if (view === "dayGridMonth") {
    const { from, to } = monthRangeInTimeZone(anchor, timeZone);
    return { from: toIsoUtc(from), to: toIsoUtc(to) };
  }
  if (view === "resourceTimeGridDay" || view === "timeGridDay") {
    const { from, to } = dayRangeInTimeZone(anchor, timeZone);
    return { from: toIsoUtc(from), to: toIsoUtc(to) };
  }
  const { from, to } = weekRangeInTimeZone(anchor, timeZone);
  return { from: toIsoUtc(from), to: toIsoUtc(to) };
}

export function ManagerCalendar(props: ManagerCalendarProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const mode: ManagerSurfaceMode = props.mode ?? "global";
  const leadId = props.leadId ?? null;

  const { isCompact, isMobile, view, setView } = useMobileCalendarView(
    props.initialView ?? "timeGridWeek",
  );
  const { boxRef, fit } = useCalendarFit(view);
  const [anchorDate, setAnchorDate] = useState<Date>(props.initialDate ?? new Date());
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(() =>
    readStoredLocationId(),
  );
  const [venueFilter, setVenueFilter] = useState<string[]>([]);
  const [focusedInstructorId, setFocusedInstructorId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<LessonDialogState | null>(null);
  const [error, setError] = useState<AccessibleError | null>(null);
  const [optimisticEvents, setOptimisticEvents] = useState<ManagerCalendarEvent[] | null>(null);
  // Шторка сделки: на широком экране открыта сразу, на телефоне её открывает кнопка в шапке.
  const [dealDrawerOpen, setDealDrawerOpen] = useState<boolean>(
    () => mode === "deal" && !isCompact,
  );
  // Вкладка шторки живёт здесь, а не внутри неё: на телефоне шторка закрывается, чтобы показать
  // урок, и `Sheet` при закрытии размонтирует содержимое. Иначе менеджер, работавший в учебном
  // плане, возвращался бы на «Деньги» после каждого урока.
  const [dealDrawerTab, setDealDrawerTab] = useState<DealDrawerTab>("money");

  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: () => api.getSession(),
  });

  const locationsQuery = useQuery({
    queryKey: ["locations"],
    queryFn: () => api.listLocations(),
    enabled: sessionQuery.isSuccess,
    staleTime: 5 * 60_000,
  });

  const venuesQuery = useQuery({
    queryKey: ["venues"],
    queryFn: () => api.listVenues(),
    enabled: sessionQuery.isSuccess,
  });

  const instructorsQuery = useQuery({
    queryKey: ["instructors"],
    queryFn: () => api.listInstructors(),
    enabled: sessionQuery.isSuccess,
  });

  const dealContextQuery = useQuery({
    queryKey: ["deal-context", leadId],
    queryFn: () => api.getDealContext(leadId as number),
    enabled: mode === "deal" && leadId !== null && sessionQuery.isSuccess,
  });

  const selectedInstructorId =
    mode === "deal" ? (dealContextQuery.data?.instructorId ?? null) : null;

  // Локация — контекст календаря, а не фильтр поверх него: она задаёт пояс сетки, границы
  // периода, набор площадок и набор инструкторов. В карточке amo её диктует сделка.
  const locations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
  const dealLocationId =
    mode === "deal" && dealContextQuery.data?.locationElementId != null
      ? String(dealContextQuery.data.locationElementId)
      : null;
  const activeLocation = useMemo(
    () => resolveActiveLocation({ locations, selectedId: selectedLocationId, dealLocationId }),
    [locations, selectedLocationId, dealLocationId],
  );
  const activeLocationId = activeLocation?.id ?? null;
  const calendarTimeZone =
    activeLocation?.timezone ??
    (mode === "deal" ? (dealContextQuery.data?.instructorTimezone ?? null) : null) ??
    getDeviceTimeZone();

  const range = useMemo(
    () => rangeFor(view, anchorDate, calendarTimeZone),
    [view, anchorDate, calendarTimeZone],
  );

  const calendarQuery = useQuery({
    queryKey: ["calendar", range.from, range.to, selectedInstructorId],
    queryFn: () =>
      api.getCalendar({
        ...range,
        ...(selectedInstructorId ? { instructorId: selectedInstructorId } : {}),
      }),
    enabled:
      sessionQuery.isSuccess &&
      (mode !== "deal" || (dealContextQuery.isSuccess && selectedInstructorId !== null)),
  });

  // Долг в шапке; DealMoneyPanel в шторке читает тот же ключ из кэша.
  const dealLedgerQuery = useQuery({
    queryKey: ["deal-ledger", leadId],
    queryFn: () => api.getDealLedger(leadId as number),
    enabled: mode === "deal" && leadId !== null && sessionQuery.isSuccess,
  });

  // Поддомен аккаунта amo для ссылок «Открыть в amoCRM»: зашитый www.amocrm.ru вёл в никуда.
  const amoAccountQuery = useQuery({
    queryKey: ["amo-account"],
    queryFn: () => api.getAmoAccount(),
    enabled: sessionQuery.isSuccess,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const dealLessonsQuery = useQuery({
    queryKey: ["deal-lessons", leadId],
    queryFn: async () => {
      if (leadId === null) {
        return { enrollments: [], lessons: [] as ScheduledLessonRecord[] };
      }
      const enrollments = await api.listEnrollmentsByLead(leadId);
      const nested = await Promise.all(enrollments.map((e) => api.listEnrollmentLessons(e.id)));
      return { enrollments, lessons: nested.flat() };
    },
    enabled: mode === "deal" && leadId !== null && sessionQuery.isSuccess,
  });

  const globalUnscheduledQuery = useQuery({
    queryKey: ["unscheduled-lessons"],
    queryFn: () => api.listUnscheduledLessons(),
    enabled: mode === "global" && sessionQuery.isSuccess,
  });

  const serverEvents = calendarQuery.data?.events ?? [];
  const baseEvents = optimisticEvents ?? serverEvents;
  const allVenues = useMemo(() => venuesQuery.data ?? [], [venuesQuery.data]);
  const venueLocations = useMemo(() => venueLocationLookup(allVenues), [allVenues]);
  const locationEvents = useMemo(
    () => filterEventsByLocation(baseEvents, venueLocations, activeLocationId),
    [baseEvents, venueLocations, activeLocationId],
  );
  const venueFilteredEvents = useMemo(
    () => filterEventsByVenues(locationEvents, venueFilter),
    [locationEvents, venueFilter],
  );
  const filteredEvents = useMemo(
    () =>
      focusedInstructorId
        ? venueFilteredEvents.filter((event) => event.instructorId === focusedInstructorId)
        : venueFilteredEvents,
    [focusedInstructorId, venueFilteredEvents],
  );
  const snapshots = useMemo(
    () => computeCapacitySnapshots(venueFilteredEvents),
    [venueFilteredEvents],
  );
  // Инструкторы острова целиком — из них выбирают замену при передаче сделки. Ресурсная сетка
  // в карточке amo сужается до одного, но список кандидатов от этого сужаться не должен.
  const locationInstructors = useMemo(
    () => filterInstructorsByLocation(instructorsQuery.data ?? [], activeLocationId),
    [instructorsQuery.data, activeLocationId],
  );
  const instructors = useMemo(
    () =>
      locationInstructors.filter(
        (instructor) => mode !== "deal" || instructor.id === selectedInstructorId,
      ),
    [locationInstructors, mode, selectedInstructorId],
  );
  const venues = useMemo(
    () => filterVenuesByLocation(allVenues, activeLocationId),
    [allVenues, activeLocationId],
  );
  const instructorColors = useMemo(
    () =>
      new Map(instructors.map((instructor) => [instructor.id, paintForInstructor(instructor.id)])),
    [instructors],
  );

  const calendarEvents = useMemo(
    () => toCalendarEvents(filteredEvents, snapshots, instructorColors, calendarTimeZone),
    [filteredEvents, snapshots, instructorColors, calendarTimeZone],
  );
  const resources = useMemo(() => toCalendarResources(instructors), [instructors]);

  const unscheduledDealLessons = useMemo(() => {
    if (mode !== "deal") {
      return [] as ScheduledLessonRecord[];
    }
    // По одному уроку на зачисление — следующему по порядку. Уроки курса идут строго от первого
    // до последнего, и выбирать среди них «любой» значило записывать третий раньше первого.
    const all = dealLessonsQuery.data?.lessons ?? [];
    const byEnrollment = new Map<string, ScheduledLessonRecord[]>();
    for (const lesson of all) {
      const list = byEnrollment.get(lesson.enrollmentId) ?? [];
      list.push(lesson);
      byEnrollment.set(lesson.enrollmentId, list);
    }
    const next: ScheduledLessonRecord[] = [];
    for (const siblings of byEnrollment.values()) {
      const candidate = nextLessonToSchedule(
        siblings.map((lesson) => ({
          lessonId: lesson.id,
          lessonOrder: lesson.lessonOrder,
          status: lesson.status,
          startTime: lesson.startTime ?? null,
          durationMinutes: lesson.durationMinutes,
        })),
      );
      const found = candidate ? siblings.find((lesson) => lesson.id === candidate.lessonId) : null;
      if (found) next.push(found);
    }
    return next;
  }, [mode, dealLessonsQuery.data]);

  // Пока эта ошибка молчала, сорвавшийся разбор ответа выглядел как «зачислений нет»,
  // и шторка бесконечно предлагала создать уже созданный план.
  useEffect(() => {
    if (!dealLessonsQuery.isError) return;
    setError(toAccessibleError(dealLessonsQuery.error));
  }, [dealLessonsQuery.isError, dealLessonsQuery.error]);

  const hasDealEnrollment = (dealLessonsQuery.data?.enrollments.length ?? 0) > 0;
  /*
   * Курсы, по которым на сделке уже есть план: остальные привязанные курсы — докупка,
   * и шторка предлагает создать по ним уроки.
   *
   * Проведённый курс отсюда не выпадает. Раньше считались только активные зачисления, и
   * завершённая «Пробная» снова показывалась как «докуплен курс»; менеджер соглашался, и на
   * сделке появлялся второй план на тот же курс (замерено на 37638073). `terminated`
   * намеренно не в счёт: расторгнутый план отменён, и тот же курс можно продать заново.
   */
  const enrolledCourseIds = useMemo(
    () =>
      (dealLessonsQuery.data?.enrollments ?? [])
        .filter((enrollment) => {
          const status = enrollment.status ?? enrollment.state ?? "active";
          return status === "active" || status === "completed";
        })
        .map((enrollment) => enrollment.amoCourseElementId)
        .filter((id): id is number => id !== undefined),
    [dealLessonsQuery.data],
  );
  // Названия курсов по элементу каталога: их читают и учебный план, и предписание об оплате.
  const courseNamesByElement = useMemo(
    () =>
      new Map(
        (dealContextQuery.data?.courses ?? []).map((course) => [
          course.elementId,
          course.name ?? `Курс ${course.elementId}`,
        ]),
      ),
    [dealContextQuery.data],
  );

  const enrollmentCourseNames = useMemo(
    () =>
      new Map(
        (dealLessonsQuery.data?.enrollments ?? []).map((enrollment) => [
          enrollment.id,
          (enrollment.amoCourseElementId !== undefined
            ? courseNamesByElement.get(enrollment.amoCourseElementId)
            : undefined) ?? "Курс",
        ]),
      ),
    [courseNamesByElement, dealLessonsQuery.data],
  );
  const dealCreationBlocked =
    mode === "deal" &&
    !hasDealEnrollment &&
    dealContextQuery.data !== undefined &&
    (dealContextQuery.data.missingDealData.length > 0 ||
      dealContextQuery.data.venueId === null ||
      dealContextQuery.data.instructorId === null);

  const bookableUnscheduled = useMemo((): UnscheduledLesson[] => {
    if (mode === "global") {
      return globalUnscheduledQuery.data ?? [];
    }
    const allLessons = dealLessonsQuery.data?.lessons ?? [];
    return unscheduledDealLessons.map((l) => ({
      lessonId: l.id,
      enrollmentId: l.enrollmentId,
      lessonOrder: l.lessonOrder,
      status: l.status,
      version: l.version,
      durationMinutes: l.durationMinutes,
      instructorId: l.instructorId,
      clientName: dealContextQuery.data?.dealName ?? null,
      courseTitle: enrollmentCourseNames.get(l.enrollmentId) ?? null,
      courseCode: null,
      // Всего уроков в этом курсе, а не во всей сделке: прогресс «2/4» считается по плану курса.
      courseTotalLessons: allLessons.filter((x) => x.enrollmentId === l.enrollmentId).length,
      coursePriceAmount: null,
      coursePriceCurrency: null,
      amoLeadId: leadId,
      enrollmentStatus: "active",
    }));
  }, [
    mode,
    globalUnscheduledQuery.data,
    unscheduledDealLessons,
    dealLessonsQuery.data,
    dealContextQuery.data,
    enrollmentCourseNames,
    leadId,
  ]);

  const invalidateCalendar = useCallback(async () => {
    setOptimisticEvents(null);
    await queryClient.invalidateQueries({ queryKey: ["calendar"] });
    if (mode === "deal") {
      await queryClient.invalidateQueries({ queryKey: ["deal-lessons"] });
    } else {
      await queryClient.invalidateQueries({ queryKey: ["unscheduled-lessons"] });
    }
  }, [queryClient, mode]);

  const scheduleMutation = useMutation({
    mutationFn: (input: {
      lessonId: string;
      startsAt: string;
      venueId: string;
      instructorId: string;
      durationMinutes?: number;
      capacityOverrideReason?: string;
    }) =>
      api.scheduleLesson(input.lessonId, {
        startsAt: input.startsAt,
        venueId: input.venueId,
        instructorId: input.instructorId,
        idempotencyKey: createIdempotencyKey("schedule"),
        ...(input.durationMinutes !== undefined ? { durationMinutes: input.durationMinutes } : {}),
        ...(input.capacityOverrideReason
          ? { capacityOverrideReason: input.capacityOverrideReason }
          : {}),
      }),
    onSuccess: async () => {
      setDialog(null);
      setError(null);
      await invalidateCalendar();
    },
    onError: (err) => {
      setOptimisticEvents(null);
      setError(toAccessibleError(err));
    },
  });

  const createDealEnrollmentMutation = useMutation({
    mutationFn: (courseElementId?: number) => {
      if (leadId === null) {
        throw new ApiClientError("Deal id is required", {
          statusCode: 422,
          code: "missing_deal",
        });
      }
      return api.createDealEnrollment(
        leadId,
        createIdempotencyKey("deal-enrollment"),
        courseElementId,
      );
    },
    onSuccess: async (result) => {
      setError(null);
      await invalidateCalendar();
      const first = result.lessons[0];
      if (!first) return;
      // Диалог записи откроется поверх; на узком экране шторка при этом уступает место.
      if (isCompact) setDealDrawerOpen(false);
      const context = dealContextQuery.data;
      const unscheduled: UnscheduledLesson = {
        lessonId: first.id,
        enrollmentId: first.enrollmentId,
        lessonOrder: first.lessonOrder,
        status: first.status,
        version: first.version,
        durationMinutes: first.durationMinutes,
        instructorId: first.instructorId,
        clientName: null,
        courseTitle: context?.courseName ?? null,
        courseCode: null,
        courseTotalLessons: result.lessons.length,
        coursePriceAmount: null,
        coursePriceCurrency: null,
        amoLeadId: leadId,
        enrollmentStatus: "active",
      };
      setDialog({ kind: "create", unscheduled, lessonId: first.id });
    },
    onError: (err) => setError(toAccessibleError(err)),
  });

  const rescheduleMutation = useMutation({
    mutationFn: (input: {
      lessonId: string;
      expectedVersion: number;
      startsAt: string;
      reason: string;
      venueId?: string;
      instructorId?: string;
      durationMinutes?: number;
      capacityOverrideReason?: string;
      snapshot: ManagerCalendarEvent[];
    }) =>
      api.rescheduleLesson(input.lessonId, {
        expectedVersion: input.expectedVersion,
        startsAt: input.startsAt,
        reason: input.reason,
        idempotencyKey: createIdempotencyKey("reschedule"),
        ...(input.venueId !== undefined ? { venueId: input.venueId } : {}),
        ...(input.instructorId !== undefined ? { instructorId: input.instructorId } : {}),
        ...(input.durationMinutes !== undefined ? { durationMinutes: input.durationMinutes } : {}),
        ...(input.capacityOverrideReason
          ? { capacityOverrideReason: input.capacityOverrideReason }
          : {}),
      }),
    onMutate: async (input) => {
      setError(null);
      setOptimisticEvents(input.snapshot);
    },
    onSuccess: async () => {
      setDialog(null);
      setError(null);
      await invalidateCalendar();
    },
    onError: (err, variables) => {
      // Server conflict/stale: restore original events and surface accessible error
      setOptimisticEvents(null);
      void queryClient.setQueryData(["calendar", range.from, range.to], (prev: unknown) => {
        if (!prev || typeof prev !== "object") {
          return prev;
        }
        return { ...prev, events: variables.snapshot };
      });
      setError(toAccessibleError(err));
      // Close overlays so the live region error is not trapped under dialogs
      setDialog(null);
      if (isStaleOrConflict(err)) {
        void invalidateCalendar();
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (input: { lessonId: string; expectedVersion: number; reason: string }) =>
      api.cancelLesson(input.lessonId, {
        expectedVersion: input.expectedVersion,
        reason: input.reason,
        idempotencyKey: createIdempotencyKey("cancel"),
      }),
    onSuccess: async () => {
      setDialog(null);
      await invalidateCalendar();
    },
    onError: (err) => setError(toAccessibleError(err)),
  });

  const noShowMutation = useMutation({
    mutationFn: (input: { lessonId: string; expectedVersion: number; reason: string }) =>
      api.markNoShow(input.lessonId, {
        expectedVersion: input.expectedVersion,
        reason: input.reason,
        idempotencyKey: createIdempotencyKey("no-show"),
      }),
    onSuccess: async () => {
      setDialog(null);
      await invalidateCalendar();
    },
    onError: (err) => setError(toAccessibleError(err)),
  });

  const findEvent = useCallback(
    (lessonId: string): ManagerCalendarEvent | undefined =>
      filteredEvents.find((e) => e.lessonId === lessonId) ??
      serverEvents.find((e) => e.lessonId === lessonId),
    [filteredEvents, serverEvents],
  );

  const handleEventDrop = useCallback(
    (info: Calendar.EventDropInfo) => {
      const lessonId = String(info.event.id);
      const current = findEvent(lessonId);
      if (!current || current.status !== "scheduled") {
        info.revert();
        return;
      }
      const previousSnapshot = serverEvents.map((e) => ({ ...e }));
      // Сетка отдаёт настенное время площадки в локальных полях: `toISOString()` прочитал бы
      // эти часы поясом браузера и сдвинул урок.
      const startsAt = wallClockToIso(info.event.start, calendarTimeZone);
      const newInstructorId =
        info.newResource?.id !== undefined
          ? String(info.newResource.id)
          : (current.instructorId ?? undefined);
      const optimistic = previousSnapshot.map((e) =>
        e.lessonId === lessonId
          ? {
              ...e,
              startTime: startsAt,
              endTime: wallClockToIso(info.event.end, calendarTimeZone),
              instructorId: newInstructorId ?? e.instructorId,
              instructorName:
                instructors.find((i) => i.id === newInstructorId)?.name ?? e.instructorName,
            }
          : e,
      );
      setOptimisticEvents(optimistic);
      rescheduleMutation.mutate({
        lessonId,
        expectedVersion: current.version,
        startsAt,
        reason: "drag-reschedule",
        ...(newInstructorId !== undefined ? { instructorId: newInstructorId } : {}),
        snapshot: previousSnapshot,
      });
    },
    [findEvent, instructors, rescheduleMutation, serverEvents, calendarTimeZone],
  );

  const handleEventResize = useCallback(
    (info: Calendar.EventResizeInfo) => {
      const lessonId = String(info.event.id);
      const current = findEvent(lessonId);
      if (!current || current.status !== "scheduled") {
        info.revert();
        return;
      }
      const previousSnapshot = serverEvents.map((e) => ({ ...e }));
      const startsAt = wallClockToIso(info.event.start, calendarTimeZone);
      const durationMinutes = Math.max(
        15,
        Math.round((info.event.end.getTime() - info.event.start.getTime()) / 60_000),
      );
      const optimistic = previousSnapshot.map((e) =>
        e.lessonId === lessonId
          ? {
              ...e,
              startTime: startsAt,
              endTime: wallClockToIso(info.event.end, calendarTimeZone),
              durationMinutes,
            }
          : e,
      );
      setOptimisticEvents(optimistic);
      rescheduleMutation.mutate({
        lessonId,
        expectedVersion: current.version,
        startsAt,
        reason: "resize",
        durationMinutes,
        snapshot: previousSnapshot,
      });
    },
    [findEvent, rescheduleMutation, serverEvents, calendarTimeZone],
  );

  const calendarOptions = useMemo<Calendar.Options>(
    () => ({
      view,
      date: anchorDate,
      resources,
      events: calendarEvents,
      editable: true,
      selectable: true,
      nowIndicator: true,
      firstDay: 1,
      // Сетка понимает только смещение, а не имя зоны: «Asia/Makassar» она молча меняла на
      // часовой пояс устройства, и календарь Бали, открытый из Москвы, рисовал уроки на пять
      // часов не там, где они есть.
      timeZone: utcOffsetLabel(anchorDate, calendarTimeZone),
      slotMinTime: fit.slotMinTime,
      slotMaxTime: fit.slotMaxTime,
      slotDuration: "00:30",
      // Слот подгоняется под коробку, месяц делит её строками поровну (§3 frontend-architecture).
      slotHeight: fit.slotHeight,
      dayMaxEvents: true,
      // Ресурсные колонки нужны только в дне; в неделе они множатся на семь.
      filterEventsWithResources: view === "resourceTimeGridDay",
      allDaySlot: false,
      locale: "ru-RU",
      // Тулбар библиотеки пуст: периодом и видом управляет CalendarToolbar,
      // а сетка получает всю высоту экрана.
      headerToolbar: { start: "", center: "", end: "" },
      height: "100%",
      datesSet: (info) => {
        setAnchorDate((prev) => {
          if (prev.getTime() === info.start.getTime()) {
            return prev;
          }
          // Ignore tiny shifts from internal view recalculation within the same local day
          if (
            prev.getFullYear() === info.start.getFullYear() &&
            prev.getMonth() === info.start.getMonth() &&
            prev.getDate() === info.start.getDate() &&
            (view === "resourceTimeGridDay" || view === "timeGridDay")
          ) {
            return prev;
          }
          if (view === "timeGridWeek") {
            const prevWeek = weekRangeInTimeZone(prev, calendarTimeZone).from.getTime();
            const nextWeek = weekRangeInTimeZone(info.start, calendarTimeZone).from.getTime();
            if (prevWeek === nextWeek) {
              return prev;
            }
          }
          if (view === "dayGridMonth") {
            const next = new Date((info.start.getTime() + info.end.getTime()) / 2);
            if (prev.getFullYear() === next.getFullYear() && prev.getMonth() === next.getMonth()) {
              return prev;
            }
            return next;
          }
          return info.start;
        });
      },
      eventClick: (info) => {
        const lessonId = String(info.event.id);
        const event = findEvent(lessonId);
        if (event) {
          setDialog({ kind: "detail", lessonId, event });
        }
      },
      dateClick: (info) => {
        const draft: LessonDialogState = {
          kind: "create",
          draftStartsAt: wallClockToIso(info.date, calendarTimeZone),
        };
        if (info.resource) {
          setDialog({ ...draft, draftInstructorId: String(info.resource.id) });
        } else {
          setDialog(draft);
        }
      },
      select: (info) => {
        const draft: LessonDialogState = {
          kind: "create",
          draftStartsAt: wallClockToIso(info.start, calendarTimeZone),
          draftEndsAt: wallClockToIso(info.end, calendarTimeZone),
        };
        if (info.resource) {
          setDialog({ ...draft, draftInstructorId: String(info.resource.id) });
        } else {
          setDialog(draft);
        }
      },
      eventDrop: handleEventDrop,
      eventResize: handleEventResize,
      eventContent: (arg) => {
        const ext = arg.event.extendedProps as {
          lesson?: ManagerCalendarEvent;
          displayTime?: string;
          displayEndTime?: string;
          venueTime?: string;
          displayTimeZone?: string;
          venueTimeZone?: string;
          capacityOccupied?: number;
          capacityTotal?: number;
          instructorInitials?: string | null;
          instructorStripe?: string | null;
        };
        const lesson = ext.lesson;
        const span = ext.displayEndTime
          ? `${ext.displayTime}–${ext.displayEndTime}`
          : (ext.displayTime ?? arg.timeText);
        const capacity = `${ext.capacityOccupied ?? "?"}/${ext.capacityTotal ?? "?"}`;

        // Кружок инструктора: цвет и инициалы опознают человека без легенды.
        const avatar =
          ext.instructorInitials && ext.instructorStripe
            ? `<span class="pwz-card-av" style="background:${escapeHtml(ext.instructorStripe)}">${escapeHtml(ext.instructorInitials)}</span>`
            : "";

        // Курс и номер урока — то, что менеджер ищет глазами; площадка и чужая
        // таймзона уходят во вторую строку и обрезаются, когда блок низкий.
        const course = [
          lesson?.courseCode,
          lesson?.lessonOrder != null
            ? `${lesson.lessonOrder}${lesson.courseTotalLessons ? `/${lesson.courseTotalLessons}` : ""}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ");
        const meta = [
          lesson?.venueName,
          ext.venueTimeZone && ext.displayTimeZone && ext.venueTimeZone !== ext.displayTimeZone
            ? `${ext.venueTime} ${shortZoneName(ext.venueTimeZone)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ");

        const name =
          lesson?.clientName ?? (typeof arg.event.title === "string" ? arg.event.title : "Урок");

        return {
          html: `<div class="pwz-card">
  <div class="pwz-card-top">
    ${avatar}
    <span class="pwz-card-time">${escapeHtml(span)}</span>
    <span class="pwz-card-cap">${escapeHtml(capacity)}</span>
  </div>
  <div class="pwz-card-name">${escapeHtml(name)}</div>
  ${course ? `<div class="pwz-card-chip">${escapeHtml(course)}</div>` : ""}
  <div class="pwz-card-meta">${escapeHtml(meta)}</div>
</div>`,
        };
      },
    }),
    [
      view,
      anchorDate,
      resources,
      calendarEvents,
      findEvent,
      handleEventDrop,
      handleEventResize,
      calendarTimeZone,
      fit,
    ],
  );

  const selectedEvent = dialog?.event ?? (dialog?.lessonId ? findEvent(dialog.lessonId) : null);
  const selectedCap = selectedEvent
    ? capacityForEvent(selectedEvent, snapshots)
    : { occupied: 0, capacity: 1 };

  const busy =
    scheduleMutation.isPending ||
    createDealEnrollmentMutation.isPending ||
    rescheduleMutation.isPending ||
    cancelMutation.isPending ||
    noShowMutation.isPending;

  const submitForm = (values: LessonFormValues) => {
    // Форма собирает настенное время площадки, а не браузера: читаем его тем же поясом.
    const selectedVenue = venues.find((v) => v.id === values.venueId);
    const venueTimeZone = selectedVenue?.timezone ?? calendarTimeZone;
    const startsAt = wallClockToIso(new Date(values.startsAtLocal), venueTimeZone);
    if (dialog?.kind === "create") {
      const fromUnscheduled =
        dialog.unscheduled && "lessonId" in dialog.unscheduled
          ? dialog.unscheduled.lessonId
          : dialog.unscheduled && "id" in dialog.unscheduled
            ? dialog.unscheduled.id
            : undefined;
      const lessonId =
        values.selectedLessonId ??
        fromUnscheduled ??
        dialog.lessonId ??
        bookableUnscheduled[0]?.lessonId;
      if (!lessonId) {
        setError({
          id: "err-no-lesson",
          message:
            mode === "deal"
              ? "На сделке нет незапланированного урока. Сначала откройте урок зачисления."
              : "Выберите незапланированный урок для записи.",
          code: "missing_lesson",
          retryable: false,
        });
        return;
      }
      scheduleMutation.mutate({
        lessonId,
        startsAt,
        venueId: values.venueId,
        instructorId: values.instructorId,
        durationMinutes: values.durationMinutes,
        ...(values.requireCapacityOverride && values.capacityOverrideReason.trim()
          ? { capacityOverrideReason: values.capacityOverrideReason.trim() }
          : {}),
      });
      return;
    }

    if ((dialog?.kind === "edit" || dialog?.kind === "reschedule") && selectedEvent) {
      const previousSnapshot = serverEvents.map((e) => ({ ...e }));
      const optimistic = previousSnapshot.map((e) =>
        e.lessonId === selectedEvent.lessonId
          ? {
              ...e,
              startTime: startsAt,
              durationMinutes: values.durationMinutes,
              venueId: values.venueId,
              instructorId: values.instructorId,
              venueName: selectedVenue?.name ?? e.venueName,
              venueTimezone: venueTimeZone,
              instructorName:
                instructors.find((i) => i.id === values.instructorId)?.name ?? e.instructorName,
            }
          : e,
      );
      rescheduleMutation.mutate({
        lessonId: selectedEvent.lessonId,
        expectedVersion: selectedEvent.version,
        startsAt,
        reason:
          values.reason.trim() ||
          (dialog.kind === "reschedule" ? "manager-reschedule" : "manager-edit"),
        venueId: values.venueId,
        instructorId: values.instructorId,
        durationMinutes: values.durationMinutes,
        snapshot: previousSnapshot,
        ...(values.requireCapacityOverride && values.capacityOverrideReason.trim()
          ? { capacityOverrideReason: values.capacityOverrideReason.trim() }
          : {}),
      });
      setOptimisticEvents(optimistic);
    }
  };

  const retryAll = () => {
    void sessionQuery.refetch();
    void venuesQuery.refetch();
    void instructorsQuery.refetch();
    void invalidateCalendar();
  };

  if (sessionQuery.isError) {
    const failure = toAccessibleError(sessionQuery.error);
    return <SignInScreen reason={signInReasonFor(failure.code)} onRetry={retryAll} />;
  }

  // Карточки в сетке живут в блоке фиксированной высоты и умеют ужиматься;
  // в месяце высота автоматическая, поэтому container-запросы там не включаем.
  const calendarSurfaceClass = `pwz-cal--chromeless ${
    view === "dayGridMonth" ? "pwz-cal--month" : "pwz-cal--grid"
  } ${view === "timeGridWeek" ? "pwz-cal--week" : ""} ${isMobile ? "pwz-cal--mobile-day" : ""}`;

  const calendarError =
    calendarQuery.isError && !calendarQuery.isFetching
      ? toAccessibleError(calendarQuery.error)
      : null;

  const ledgerBalance = dealLedgerQuery.data?.balance;
  const debtLabel = !ledgerBalance
    ? null
    : ledgerBalance.debtLocal !== null && ledgerBalance.debtLocal > 0
      ? `${ledgerBalance.debtLocal.toLocaleString("ru-RU")} ${dealLedgerQuery.data?.currency ?? ""}`.trim()
      : ledgerBalance.paymentStatus === "paid"
        ? "оплачено"
        : ledgerBalance.paymentStatus === "overpaid"
          ? "переплата"
          : null;

  const dealSummary: DealHeaderSummary | undefined =
    mode === "deal" && leadId !== null
      ? {
          name: dealContextQuery.data?.dealName ?? null,
          debtLabel,
          onOpen: () => setDealDrawerOpen(true),
        }
      : undefined;

  const calendarLoading =
    calendarQuery.isLoading ||
    instructorsQuery.isLoading ||
    sessionQuery.isLoading ||
    dealContextQuery.isLoading;

  return (
    <SurfaceShell
      testId="manager-calendar-root"
      className="h-dvh min-h-0 overflow-hidden"
      onRetry={retryAll}
      bleed
      chrome={
        <CalendarToolbar
          mode={mode}
          leadId={leadId}
          view={view}
          isCompact={isCompact}
          isMobile={isMobile}
          rangeLabel={formatRangeLabel(view, anchorDate)}
          locations={locations}
          activeLocationId={activeLocationId}
          timeZone={calendarTimeZone}
          venues={venues}
          selectedVenueIds={venueFilter}
          instructors={instructors}
          instructorColors={instructorColors}
          focusedInstructorId={focusedInstructorId}
          {...(dealSummary ? { deal: dealSummary } : {})}
          onViewChange={setView}
          onLocationChange={(locationId) => {
            setSelectedLocationId(locationId);
            storeLocationId(locationId);
          }}
          createDisabled={dealCreationBlocked}
          onVenueFilterChange={setVenueFilter}
          onInstructorFocus={setFocusedInstructorId}
          onPrev={() => setAnchorDate((prev) => shiftAnchor(view, prev, -1))}
          onNext={() => setAnchorDate((prev) => shiftAnchor(view, prev, 1))}
          onToday={() => setAnchorDate(new Date())}
          unscheduledCount={bookableUnscheduled.length}
          // Счётчик показывает N — значит и открывать должен эти N, а не форму для первого из
          // них: в общем календаре они принадлежат разным клиентам и разным сделкам.
          onOpenUnscheduled={() => setDialog({ kind: "unscheduled-queue" })}
          onCreate={() => {
            const first = bookableUnscheduled[0];
            if (first) {
              setDialog({ kind: "create", unscheduled: first, lessonId: first.lessonId });
            } else if (mode === "deal" && leadId !== null && !hasDealEnrollment) {
              const context = dealContextQuery.data;
              const trainingCourses = context?.courses.filter((course) => !course.isAddon) ?? [];
              if (context && context.courseElementId === null && trainingCourses.length > 1) {
                // Несколько курсов: выбор делает менеджер в шторке, а не система наугад.
                setDealDrawerOpen(true);
              } else {
                createDealEnrollmentMutation.mutate(undefined);
              }
            } else {
              setDialog({ kind: "create" });
            }
          }}
          onRefresh={() => {
            void invalidateCalendar();
          }}
        />
      }
    >
      {/* Ошибки — тостом поверх сетки и шторки: рабочая поверхность не сдвигается (правило 2). */}
      {error || calendarError ? (
        <div className="pointer-events-none fixed inset-x-0 top-16 z-[60] flex flex-col items-center gap-2 px-3">
          <div className="pointer-events-auto w-full max-w-xl empty:hidden">
            <ErrorBanner error={error} onDismiss={() => setError(null)} onRetry={retryAll} />
          </div>
          {calendarError ? (
            <div className="pointer-events-auto w-full max-w-xl">
              <ErrorBanner error={calendarError} onRetry={() => void calendarQuery.refetch()} />
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        className="relative flex min-h-0 min-w-0 flex-1 flex-col p-2 sm:p-4"
        data-testid="calendar-host"
      >
        <div ref={boxRef} className="min-h-0 min-w-0 flex-1">
          <EventCalendarAdapter
            options={calendarOptions}
            className={`h-full ${calendarSurfaceClass}`}
            data-testid="manager-event-calendar"
          />
        </div>
        {calendarLoading ? (
          <div className="absolute inset-0 z-10 grid place-items-center bg-pad-100/70">
            <LoadingState
              label="Загрузка расписания…"
              testId="manager-loading"
              minHeightClassName="min-h-0"
            />
          </div>
        ) : calendarQuery.isError ? (
          <div className="absolute inset-0 z-10 grid place-items-center bg-pad-100/80">
            <EmptyState
              title="Не удалось загрузить календарь"
              description="Проверьте сеть и нажмите «Повторить»."
              testId="manager-calendar-empty-error"
              action={
                <Button type="button" onClick={() => void calendarQuery.refetch()}>
                  Повторить
                </Button>
              }
            />
          </div>
        ) : filteredEvents.length === 0 && bookableUnscheduled.length === 0 ? (
          <p
            className="pointer-events-none absolute inset-x-0 top-14 z-10 mx-auto w-fit rounded-full bg-ink-950/80 px-4 py-2 text-center text-sm text-white"
            data-testid="manager-calendar-empty"
          >
            Нет уроков в выбранном диапазоне — запишите урок или смените период.
          </p>
        ) : null}
      </div>

      {mode === "deal" && leadId !== null ? (
        <DealDrawer
          open={dealDrawerOpen}
          onOpenChange={setDealDrawerOpen}
          tab={dealDrawerTab}
          onTabChange={setDealDrawerTab}
          leadId={leadId}
          context={dealContextQuery.data}
          hasEnrollment={hasDealEnrollment}
          enrolledCourseIds={enrolledCourseIds}
          enrollmentCourseNames={enrollmentCourseNames}
          instructors={locationInstructors}
          timeZone={calendarTimeZone}
          settlementCourseNames={courseNamesByElement}
          amoBaseUrl={amoAccountQuery.data?.baseUrl ?? null}
          lessons={dealLessonsQuery.data?.lessons ?? []}
          venues={venues}
          enrollmentBusy={createDealEnrollmentMutation.isPending}
          onCreateEnrollment={(courseElementId) =>
            createDealEnrollmentMutation.mutate(courseElementId)
          }
          onScheduleLesson={(next) => {
            // Шторку закрываем только там, где она занимает весь экран: на широком она справа,
            // диалог по центру, и прятать контекст сделки ради урока незачем.
            if (isCompact) setDealDrawerOpen(false);
            setDialog({ kind: "create", unscheduled: next, lessonId: next.id });
          }}
          onOpenLesson={(lesson) => {
            // Урок уже в сетке: открываем ту же карточку, что и по клику в календаре.
            const event = findEvent(lesson.id);
            if (event) {
              if (isCompact) setDealDrawerOpen(false);
              setDialog({ kind: "detail", lessonId: lesson.id, event });
            }
          }}
          onError={(accessible) => setError(accessible)}
        />
      ) : null}

      <LessonDetailSheet
        amoBaseUrl={amoAccountQuery.data?.baseUrl ?? null}
        open={dialog?.kind === "detail"}
        event={selectedEvent ?? null}
        occupied={selectedCap.occupied}
        capacity={selectedCap.capacity}
        onClose={() => setDialog(null)}
        onEdit={() => selectedEvent && setDialog({ kind: "edit", event: selectedEvent })}
        onReschedule={() =>
          selectedEvent && setDialog({ kind: "reschedule", event: selectedEvent })
        }
        onCancel={() => selectedEvent && setDialog({ kind: "cancel", event: selectedEvent })}
        onNoShow={() => selectedEvent && setDialog({ kind: "no-show", event: selectedEvent })}
      />

      <LessonFormDialog
        open={dialog?.kind === "create" || dialog?.kind === "edit" || dialog?.kind === "reschedule"}
        mode={
          dialog?.kind === "create"
            ? "create"
            : dialog?.kind === "reschedule"
              ? "reschedule"
              : "edit"
        }
        instructors={instructors}
        venues={venues.filter((v) => v.isActive !== false)}
        timeZone={calendarTimeZone}
        event={selectedEvent ?? null}
        unscheduled={dialog?.unscheduled ?? null}
        unscheduledOptions={dialog?.kind === "create" ? bookableUnscheduled : []}
        requireLessonSelection={dialog?.kind === "create"}
        {...(dialog?.draftStartsAt ? { initialStartsAt: dialog.draftStartsAt } : {})}
        {...(dialog?.draftInstructorId ? { initialInstructorId: dialog.draftInstructorId } : {})}
        {...(dialog?.draftVenueId
          ? { initialVenueId: dialog.draftVenueId }
          : dealContextQuery.data?.venueId
            ? { initialVenueId: dealContextQuery.data.venueId }
            : {})}
        busy={busy}
        onClose={() => setDialog(null)}
        onSubmit={submitForm}
      />

      <UnscheduledQueueDialog
        open={dialog?.kind === "unscheduled-queue"}
        lessons={bookableUnscheduled}
        onClose={() => setDialog(null)}
        onPick={(lesson) =>
          setDialog({ kind: "create", unscheduled: lesson, lessonId: lesson.lessonId })
        }
      />

      <ReasonDialog
        open={dialog?.kind === "cancel"}
        title="Отменить урок"
        confirmLabel="Отменить урок"
        busy={cancelMutation.isPending}
        testId="cancel-dialog"
        onClose={() => setDialog(null)}
        onConfirm={(reason) => {
          if (!selectedEvent) return;
          cancelMutation.mutate({
            lessonId: selectedEvent.lessonId,
            expectedVersion: selectedEvent.version,
            reason,
          });
        }}
      />

      <ReasonDialog
        open={dialog?.kind === "no-show"}
        title="Отметить неявку"
        confirmLabel="Отметить неявку"
        busy={noShowMutation.isPending}
        testId="no-show-dialog"
        onClose={() => setDialog(null)}
        onConfirm={(reason) => {
          if (!selectedEvent) return;
          noShowMutation.mutate({
            lessonId: selectedEvent.lessonId,
            expectedVersion: selectedEvent.version,
            reason,
          });
        }}
      />
    </SurfaceShell>
  );
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
